import { supabase } from "./supabase";
import { config } from "./config";

// OneSignal web push. Gated on VITE_ONESIGNAL_APP_ID: if it's absent, everything
// no-ops and the app just works without push. We never touch the REST API key
// client-side — that lives only on the server for sending.

declare global {
  interface Window {
    OneSignalDeferred?: any[];
    OneSignal?: any;
  }
}

let initialized = false;

export function pushConfigured(): boolean {
  return !!config.oneSignalAppId;
}

// Read the subscription id, retrying for a while. Registering with Apple or
// Google is not instant. The old version gave up after four seconds and then
// showed the "that didn't take" troubleshooting wall to people whose phones were
// still finishing a perfectly successful setup.
async function waitForSubscriptionId(OneSignal: any, attempts = 30, gapMs = 400): Promise<string | null> {
  for (let i = 0; i < attempts; i++) {
    const id = OneSignal.User?.PushSubscription?.id;
    if (id) return id;
    await new Promise((r) => setTimeout(r, gapMs));
  }
  return null;
}

// Initialize the SDK once, on app load. Safe to call repeatedly.
export function initPush() {
  if (!pushConfigured() || initialized) return;
  initialized = true;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async (OneSignal: any) => {
    try {
      // Use OneSignal's default worker (public/OneSignalSDKWorker.js at root).
      await OneSignal.init({
        appId: config.oneSignalAppId,
        allowLocalhostAsSecureOrigin: true,
      });

      // Watch for the id changing for as long as the app is open. This listener
      // used to be attached only inside enablePush, so it existed only during
      // the one tap that turned notifications on. A subscription id that rotated
      // later (which iOS does) was never reported, the server kept sending to
      // the dead id, and the member saw "notifications are on" while receiving
      // nothing at all, with no way to find out.
      OneSignal.User?.PushSubscription?.addEventListener?.("change", (e: any) => {
        const id = e?.current?.id || OneSignal.User?.PushSubscription?.id;
        if (id) storeSubscription(id); // no-ops while signed out
      });
    } catch {
      /* init errors are non-fatal — app still works without push */
    }
  });
}

// Make sure the server is sending to the device in the person's hand.
//
// Called on every league-home load. If the id rotated while the app was closed,
// no change event was there to hear it, so this is the catch-up. The stale row
// needs no cleanup here: the next send gets it back from OneSignal as invalid
// and retireDeadDevices retires it.
export function syncCurrentDevice() {
  if (!pushConfigured()) return;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async (OneSignal: any) => {
    try {
      const uid = await currentUserId();
      if (!uid) return;
      const id = await waitForSubscriptionId(OneSignal, 8, 500);
      if (!id) return;
      // Already the registered device: don't write on every single app open.
      const { data } = await supabase
        .from("push_subscriptions")
        .select("status")
        .eq("user_id", uid)
        .eq("provider_subscription_id", id)
        .maybeSingle();
      if (data?.status === "active") return;
      await storeSubscription(id);
    } catch {
      /* never block the app on a background repair */
    }
  });
}

// The OneSignal subscription id for the device running this code right now.
function currentSubscriptionId(): string | null {
  try {
    return (window as any).OneSignal?.User?.PushSubscription?.id ?? null;
  } catch {
    return null;
  }
}

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

// A phone is registered to a person, not to a league. Writing push_status for a
// single league meant somebody in two leagues was "push ready" in one and
// "needs setup" in the other while holding the exact same working phone.
// The RLS policy memberships_update_self is scoped by user, not league, so this
// still only ever touches the caller's own rows.
async function setStatus(status: "push_ready" | "needs_setup") {
  const uid = await currentUserId();
  if (!uid) return;
  await supabase.from("league_memberships").update({ push_status: status }).eq("user_id", uid);
}

// A rough, human-readable label for the device that just subscribed. One person
// can register several (laptop browser, phone browser, installed app), and
// without a label a delivery report cannot answer the only question that
// matters: did it go to the phone, or to the laptop sitting next to it?
function deviceLabel(): string {
  const ua = window.navigator.userAgent;
  const standalone =
    (window.navigator as any).standalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches === true;
  let os = "Unknown device";
  if (/iphone|ipod/i.test(ua)) os = "iPhone";
  else if (/ipad/i.test(ua)) os = "iPad";
  else if (/android/i.test(ua)) os = "Android";
  else if (/macintosh|mac os x/i.test(ua)) os = "Mac";
  else if (/windows/i.test(ua)) os = "Windows";
  return standalone ? `${os} (home screen app)` : `${os} (browser)`;
}

async function storeSubscription(subId: string) {
  const uid = await currentUserId();
  if (!uid) return;
  await supabase.from("push_subscriptions").upsert(
    {
      user_id: uid,
      provider: "onesignal",
      provider_subscription_id: subId,
      platform: deviceLabel(),
      status: "active",
      last_confirmed_at: new Date().toISOString(),
    },
    { onConflict: "user_id,provider_subscription_id" }
  );
  await setStatus("push_ready");
}

// Put the member back to "not set up yet" so they can walk the setup flow again.
// Needed whenever the old subscription is dead: a new phone, a reinstalled app,
// or the commissioner testing the onboarding his league is about to walk through.
// RLS policy push_owner_all already scopes both writes to the caller's own rows.
export async function resetPushSetup(_leagueId?: string): Promise<void> {
  const uid = await currentUserId();
  if (!uid) return;

  // Scoped to THIS device only. It used to revoke every device the person owned,
  // which quietly killed a working phone the moment they redid setup on a second
  // one. Falling back to clearing everything is correct only when we cannot
  // identify the device we are on, since then there is nothing else to preserve.
  const currentId = currentSubscriptionId();
  if (currentId) {
    await supabase
      .from("push_subscriptions")
      .update({ status: "revoked" })
      .eq("user_id", uid)
      .eq("provider_subscription_id", currentId);
  } else {
    await supabase.from("push_subscriptions").update({ status: "revoked" }).eq("user_id", uid);
  }

  // Still push_ready if another device is registered, otherwise back to setup.
  const { count } = await supabase
    .from("push_subscriptions")
    .select("id", { count: "exact", head: true })
    .eq("user_id", uid)
    .eq("status", "active");
  await setStatus((count ?? 0) > 0 ? "push_ready" : "needs_setup");
}

// The outcome of asking a phone to turn notifications on.
//  push_ready  the device is registered and the server can reach it
//  denied      the person (or the OS) said no, and only Settings can undo it
//  pending     permission was granted but no subscription id yet, which is a
//              normal few-second wait on iOS, not a failure
//  failed      something else went wrong
export type PushOutcome = "push_ready" | "denied" | "pending" | "failed";

function nativePermission(): NotificationPermission | "unsupported" {
  return "Notification" in window ? Notification.permission : "unsupported";
}


// Ask for permission and capture the push subscription id.
export async function enablePush(_leagueId?: string): Promise<PushOutcome> {
  if (!pushConfigured()) {
    await setStatus("needs_setup");
    return "failed";
  }
  return new Promise((resolve) => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal: any) => {
      try {
        // If the id shows up later, record it then. This keeps a slow
        // registration from being lost just because we stopped watching.
        OneSignal.User?.PushSubscription?.addEventListener?.("change", (e: any) => {
          const id = e?.current?.id || OneSignal.User?.PushSubscription?.id;
          if (id) storeSubscription(id);
        });

        await OneSignal.Notifications.requestPermission();

        if (nativePermission() === "denied") {
          await setStatus("needs_setup");
          resolve("denied");
          return;
        }

        const id = await waitForSubscriptionId(OneSignal);
        if (id) {
          await storeSubscription(id);
          resolve("push_ready");
        } else {
          await setStatus("needs_setup");
          // Permission is granted but registration has not landed. Telling
          // someone to go fix their settings here would be wrong.
          resolve(nativePermission() === "granted" ? "pending" : "failed");
        }
      } catch {
        await setStatus("needs_setup");
        resolve("failed");
      }
    });
  });
}

// Re-check after a "pending" result, so the card can settle itself once the
// phone finishes registering without making the person tap anything again.
export async function recheckPush(): Promise<PushOutcome> {
  if (!pushConfigured()) return "failed";
  return new Promise((resolve) => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal: any) => {
      try {
        if (nativePermission() === "denied") { resolve("denied"); return; }
        const id = await waitForSubscriptionId(OneSignal, 10, 500);
        if (id) { await storeSubscription(id); resolve("push_ready"); return; }
        resolve(nativePermission() === "granted" ? "pending" : "failed");
      } catch {
        resolve("failed");
      }
    });
  });
}
